import React, { useState } from 'react';
import { feedbackAPI, uploadsAPI, formatApiError } from '../services/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChatCircleDots, X, PaperPlaneTilt, Camera, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) { toast.error('Please enter your feedback'); return; }
    setSending(true);
    try {
      await feedbackAPI.submit({
        category,
        message: message.trim(),
        page_url: window.location.href,
      });
      setSent(true);
      setTimeout(() => { setSent(false); setOpen(false); setMessage(''); setCategory('general'); }, 2000);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[100] w-12 h-12 bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
          data-testid="feedback-fab"
          aria-label="Send Feedback"
        >
          <ChatCircleDots className="w-5 h-5" weight="fill" />
        </button>
      )}

      {/* Feedback Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-[100] w-80 sm:w-96 animate-in slide-in-from-bottom-4 duration-200" data-testid="feedback-panel">
          <div className="bg-[#1A1A1A] border border-[#333333] rounded-xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#22C55E] to-[#16A34A] px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ChatCircleDots className="w-4 h-4 text-white" weight="fill" />
                <span className="text-white text-sm font-bold">Send Feedback</span>
              </div>
              <button
                onClick={() => { setOpen(false); setSent(false); }}
                className="text-white/60 hover:text-white transition-colors"
                data-testid="feedback-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {sent ? (
              <div className="p-6 text-center" data-testid="feedback-success">
                <div className="w-12 h-12 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-[#22C55E]" weight="bold" />
                </div>
                <p className="text-white font-semibold text-sm">Thanks for your feedback!</p>
                <p className="text-[#999999] text-xs mt-1">We'll review it and get back to you.</p>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {/* Category */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#666666] font-semibold mb-1 block">Category</label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="bg-[#111111] border-[#333333] text-white rounded-lg h-9 text-sm" data-testid="feedback-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#333333]">
                      <SelectItem value="bug">Bug Report</SelectItem>
                      <SelectItem value="feature">Feature Request</SelectItem>
                      <SelectItem value="general">General Feedback</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#666666] font-semibold mb-1 block">Message</label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={
                      category === 'bug' ? 'Describe what went wrong...' :
                      category === 'feature' ? 'What feature would you like?' :
                      'Share your thoughts...'
                    }
                    className="bg-[#111111] border-[#333333] text-white placeholder:text-[#666666] rounded-lg resize-none text-sm"
                    rows={4}
                    maxLength={2000}
                    data-testid="feedback-message"
                  />
                  <p className="text-[10px] text-[#666666] mt-1 text-right">{message.length}/2000</p>
                </div>

                {/* Submit */}
                <Button
                  onClick={handleSubmit}
                  disabled={sending || !message.trim()}
                  className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-lg h-9 text-sm font-semibold"
                  data-testid="feedback-submit"
                >
                  {sending ? 'Sending...' : (
                    <>
                      <PaperPlaneTilt className="w-3.5 h-3.5 mr-1.5" />
                      Send Feedback
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
